/**
 * Step 2 of adding a product: pick its HS code.
 *
 * The classifier (`HsClassifierPanel`) proposes a code by voting over local
 * sources and may ask a question to go deeper. The user picks a code from
 * the result path or the alternatives, or types one; a typed code is looked
 * up in the HS table first (`GET /hscodes/codes/<code>/`). The pick is
 * recorded on the classifier run (`/classify/<id>/choose/`), which helps
 * tune it.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { normaliseHsCode } from "../../lib/hsCodes";
import {
  useHscodesClassifyChooseCreate,
  useHscodesCodesRetrieve,
} from "../../services/hscodes/generated/hs-codes/hs-codes";
import HsClassifierPanel, { type HsPick } from "../HsClassifier/HsClassifierPanel";
import "./HSCodeSelectionModal.css";

export type HSCodeSuggestion = HsPick;

interface HSCodeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onConfirm: (selected: HSCodeSuggestion) => void;
  productName: string;
  productDescription?: string;
  actionLabel: string;
  isSubmitting?: boolean;
  errorMessage?: string | null;
}

const HSCodeSelectionModal = ({
  isOpen,
  onClose,
  onBack,
  onConfirm,
  productName,
  productDescription = "",
  actionLabel,
  isSubmitting = false,
  errorMessage = null,
}: HSCodeSelectionModalProps) => {
  const { t } = useTranslation(["b2b", "common"]);
  const [selected, setSelected] = useState<HsPick | null>(null);
  const [lastRunId, setLastRunId] = useState<number | null>(null);
  const [manualCode, setManualCode] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const choose = useHscodesClassifyChooseCreate();

  useEffect(() => {
    if (!isOpen) return;
    setSelected(null);
    setLastRunId(null);
    setManualCode("");
    setLocalError(null);
  }, [isOpen]);

  const trimmedManual = normaliseHsCode(manualCode.trim());
  const manualLookup = useHscodesCodesRetrieve(trimmedManual, {
    query: { enabled: isOpen && trimmedManual.length >= 2, retry: false },
  });

  if (!isOpen) return null;

  const activeError = localError || errorMessage;

  const handleConfirm = () => {
    const pick: HsPick | null = trimmedManual
      ? { code: trimmedManual, description: manualLookup.data?.description ?? "", runId: lastRunId }
      : selected;
    if (!pick) {
      setLocalError(t("hsCodeModal.errors.selectRequired"));
      return;
    }
    setLocalError(null);
    // Tell the classifier which code was used (best effort: never blocks saving).
    if (pick.runId) choose.mutate({ id: pick.runId, data: { code: pick.code } });
    onConfirm(pick);
  };

  return (
    <div className="hs-modal__overlay" onClick={onClose}>
      <div className="hs-modal__content" onClick={(e) => e.stopPropagation()}>
        <div className="hs-modal__header">
          <h2 className="hs-modal__title">{t("hsCodeModal.title")}</h2>
          <button type="button" className="hs-modal__close" onClick={onClose} aria-label={t("hsCodeModal.closeAria")}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="hs-modal__body">
          <div className="hs-modal__ai-banner">
            <div className="hs-modal__ai-icon">
              <i className="fa-solid fa-sitemap" />
            </div>
            <div>
              <h3 className="hs-modal__ai-title">{t("hsCodeModal.aiTitle")}</h3>
              <p className="hs-modal__ai-subtitle">{t("hsCodeModal.aiSubtitle", { productName })}</p>
            </div>
          </div>

          {activeError && (
            <div className="hs-modal__error" role="alert">
              <i className="fa-solid fa-circle-exclamation" />
              <span>{activeError}</span>
            </div>
          )}

          <HsClassifierPanel
            input={{ name: productName, description: productDescription }}
            selectedCode={trimmedManual ? null : (selected?.code ?? null)}
            onResult={(r) => setLastRunId(r.runId)}
            onSelect={(pick) => {
              setSelected(pick);
              setManualCode("");
              setLocalError(null);
            }}
          />

          <div className="hs-modal__manual">
            <label className="hs-modal__manual-label" htmlFor="hs-modal-manual-code">
              {t("hsCodeModal.manualLabel")}
            </label>
            <input
              id="hs-modal-manual-code"
              type="text"
              className="hs-modal__manual-input"
              placeholder={t("hsCodeModal.manualPlaceholder")}
              value={manualCode}
              onChange={(e) => {
                setManualCode(e.target.value);
                setLocalError(null);
              }}
              disabled={isSubmitting}
            />
            <p className="hs-modal__manual-hint">
              {trimmedManual.length >= 2 && manualLookup.data
                ? t("hsCodeModal.manualFound", { description: manualLookup.data.description })
                : trimmedManual.length >= 2 && manualLookup.isError
                  ? t("hsCodeModal.manualUnknown")
                  : t("hsCodeModal.manualHint")}
            </p>
          </div>
        </div>

        <div className="hs-modal__footer">
          <button
            type="button"
            className="hs-modal__button hs-modal__button--ghost"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t("common:actions.cancel")}
          </button>
          <button
            type="button"
            className="hs-modal__button hs-modal__button--secondary"
            onClick={onBack}
            disabled={isSubmitting}
          >
            {t("common:actions.back")}
          </button>
          <button
            type="button"
            className="hs-modal__button hs-modal__button--primary"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("hsCodeModal.adding") : actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HSCodeSelectionModal;
